import axiosInstance from './AxiosInstance';
import swal from "sweetalert";
import {
    loginConfirmedAction,
    Logout,
} from '../store/actions/AuthActions';

const AuthService = {
    // 1. USER LOGIN
    login: async (email, password) => {
        try {
            const response = await axiosInstance.post('/auth/systemadmin/login', {
                email,
                password
            });
            
            return {
                success: response.data.success,
                data: {
                    user: response.data.data.user,
                    accessToken: response.data.data.accessToken,
                    refreshToken: response.data.data.refreshToken
                },
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 2. USER REGISTRATION
    register: async (userData) => {
        try {
            const response = await axiosInstance.post('/auth/register', userData);
            
            return {
                success: response.data.success,
                data: {
                    user: response.data.data.user,
                    accessToken: response.data.data.accessToken,
                    refreshToken: response.data.data.refreshToken
                },
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Registration failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 3. REFRESH TOKEN
    refreshToken: async (refreshToken) => {
        try {
            const response = await axiosInstance.post('/auth/refresh-token', {
                refreshToken
            });
            
            return {
                success: response.data.success,
                data: {
                    accessToken: response.data.data.accessToken,
                    refreshToken: response.data.data.refreshToken
                },
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Token refresh failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 4. LOGOUT
    logout: async () => {
        try {
            const response = await axiosInstance.post('/auth/logout');
            
            return {
                success: response.data.success,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Logout failed'
            };
        }
    },

    // 5. FORGOT PASSWORD
    forgotPassword: async (email) => {
        try {
            const response = await axiosInstance.post('/auth/forgot-password', { email });
            
            return {
                success: response.data.success,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to send reset email',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 6. RESET PASSWORD
    resetPassword: async (token, newPassword, confirmPassword) => {
        try {
            const response = await axiosInstance.post('/auth/reset-password', {
                token,
                newPassword,
                confirmPassword
            });
            
            return {
                success: response.data.success,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Password reset failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 7. CHANGE PASSWORD
    changePassword: async (currentPassword, newPassword, confirmPassword) => {
        try {
            const response = await axiosInstance.post('/auth/change-password', {
                currentPassword,
                newPassword,
                confirmPassword
            });
            
            return {
                success: response.data.success,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Password change failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 8. GET CURRENT USER
    getCurrentUser: async () => {
        try {
            const response = await axiosInstance.get('/auth/me');
            
            return {
                success: response.data.success,
                data: response.data.data.user,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch user data',
                data: null
            };
        }
    }
};

// Legacy function exports for backward compatibility
export function signUp(email, password) {
    return AuthService.register({ email, password });
}

export function login(email, password) {
    return AuthService.login(email, password);
}

export function formatError(errorResponse) {
    const message = errorResponse?.message || errorResponse?.error?.message;
    
    // Prevent multiple SweetAlert popups by adding a delay
    setTimeout(() => {
        switch (message) {
            case 'EMAIL_EXISTS':
            case 'User already exists':
                swal("Oops", "Email already exists", "error");
                break;
            case 'EMAIL_NOT_FOUND':
            case 'User not found':
                swal("Oops", "Email not found", "error", { button: "Try Again!" });
                break;
            case 'INVALID_PASSWORD':
            case 'Invalid credentials':
            case 'Invalid password':
                swal("Oops", "Invalid Password", "error", { button: "Try Again!" });
                break;
            case 'USER_DISABLED':
            case 'Account disabled':
                swal("Oops", "Account has been disabled", "error");
                break;
            case 'INVALID_EMAIL':
            case 'Invalid email format':
                swal("Oops", "Invalid email format", "error");
                break;
            case 'TOKEN_EXPIRED':
            case 'Session expired':
                swal("Session Expired", "Please login again", "warning");
                break;
            default:
                swal("Error", message || "An error occurred", "error");
                break;
        }
    }, 100); // Small delay to prevent conflicts with loading states
}

export function saveTokenInLocalStorage(tokenDetails) {
    // Calculate expiration time (24 hours for access token)
    const expirationTime = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
    
    const userDetails = {
        ...tokenDetails,
        expireDate: new Date(expirationTime),
        expiresIn: 24 * 60 * 60 // 24 hours in seconds
    };
    
    localStorage.setItem('userDetails', JSON.stringify(userDetails));
    
    // Store refresh token separately if provided
    if (tokenDetails.refreshToken) {
        localStorage.setItem('refreshToken', tokenDetails.refreshToken);
    }
}

export function getTokenFromLocalStorage() {
    const tokenDetailsString = localStorage.getItem('userDetails');
    if (tokenDetailsString) {
        return JSON.parse(tokenDetailsString);
    }
    return null;
}

export function getRefreshTokenFromLocalStorage() {
    return localStorage.getItem('refreshToken');
}

export function removeTokenFromLocalStorage() {
    localStorage.removeItem('userDetails');
    localStorage.removeItem('refreshToken');
}

export function runLogoutTimer(dispatch, timer, navigate) {
    setTimeout(() => {
        dispatch(Logout(navigate));
        removeTokenFromLocalStorage();
    }, timer);
}

export async function checkAutoLogin(dispatch, navigate) {
    const tokenDetails = getTokenFromLocalStorage();
    
    if (!tokenDetails) {
        dispatch(Logout(navigate));
        return;
    }

    let expireDate = new Date(tokenDetails.expireDate);
    let todaysDate = new Date();

    // Check if token is expired
    if (todaysDate > expireDate) {
        // Try to refresh token
        const refreshToken = getRefreshTokenFromLocalStorage();
        if (refreshToken) {
            try {
                const refreshResult = await AuthService.refreshToken(refreshToken);
                if (refreshResult.success) {
                    // Save new tokens
                    saveTokenInLocalStorage(refreshResult.data);
                    dispatch(loginConfirmedAction(refreshResult.data));
                    
                    // Set new timer
                    const timer = (24 * 60 * 60 * 1000); // 24 hours
                    runLogoutTimer(dispatch, timer, navigate);
                    return;
                }
            } catch (error) {
                console.error('Token refresh failed:', error);
            }
        }
        
        // If refresh failed or no refresh token, logout
        dispatch(Logout(navigate));
        removeTokenFromLocalStorage();
        return;
    }
    
    // Token is still valid
    dispatch(loginConfirmedAction(tokenDetails));
    
    const timer = expireDate.getTime() - todaysDate.getTime();
    runLogoutTimer(dispatch, timer, navigate);
}

export function isLogin() {
    const tokenDetails = getTokenFromLocalStorage();
    
    if (!tokenDetails) {
        return false;
    }
    
    // Check if token is expired
    const expireDate = new Date(tokenDetails.expireDate);
    const todaysDate = new Date();
    
    return todaysDate <= expireDate;
}

// Export the AuthService as default
export default AuthService;
